import { ghostElapsedAtProgress, type GhostProgressSample } from './Ghost';
import type { LapTimer } from './LapTimer';
import { formatHandle } from '../server/handles';

export interface LivePosition {
  /** Live lap elapsed time in ms (fixed-step accumulator). */
  lapMs: number | null;
  /** Distance travelled along the lap in meters, 0..totalLength. */
  progress: number | null;
  totalLength: number;
  /** Progress samples for the player's PB lap. Drives ΔPB. */
  pbSamples: GhostProgressSample[];
  /** Progress samples for whatever the split panel is comparing against
   *  (active rival ghost, or PB when no rival). Drives the active-sector
   *  split delta. */
  comparisonSamples: GhostProgressSample[];
}

export interface LeaderboardHudRow {
  kind: 'record' | 'nearby' | 'mine' | 'empty';
  rank: number | null;
  handle: string;
  totalMs: number | null;
  deltaMs?: number | null;
  hasGhost?: boolean;
}

export interface GhostTrackerState {
  title?: string;
  label: string;
  deltaMs: number | null;
  screen?: GhostTrackerScreenPosition | null;
}

export interface GhostTrackerScreenPosition {
  x: number;
  y: number;
  pinned: boolean;
}

export interface RaceTargetState {
  label: string;
  totalMs: number | null;
  note?: string | null;
}

export class HUD {
  readonly root: HTMLElement;
  private readonly currentLapEl: HTMLElement;
  private readonly bestLapEl: HTMLElement;
  private readonly lastLapEl: HTMLElement | null;
  private readonly deltaPbEl: HTMLElement | null;
  private readonly speedEl: HTMLElement;
  private readonly driftEl: HTMLElement | null;
  private readonly driftFillEl: HTMLElement | null;
  private readonly sectorNumEl: HTMLElement;
  private readonly currentSectorEl: HTMLElement;
  private readonly bestSectorEl: HTMLElement;
  private readonly pausedEl: HTMLElement;
  private readonly ghostTrackerEl: HTMLElement | null;
  private readonly ghostTitleEl: HTMLElement | null;
  private readonly ghostLabelEl: HTMLElement | null;
  private readonly ghostDeltaEl: HTMLElement | null;
  private readonly ghostStatusEl: HTMLElement | null;
  private readonly targetRowEl: HTMLElement | null;
  private readonly targetLabelEl: HTMLElement | null;
  private readonly targetTimeEl: HTMLElement | null;
  private readonly targetNoteEl: HTMLElement | null;
  private readonly splitPanelEl: HTMLElement | null;
  private readonly splitTitleEl: HTMLElement | null;
  private readonly leaderboardBoardEl: HTMLElement | null;
  private readonly leaderboardRowsEl: HTMLElement | null;
  private readonly rivalRankEl: HTMLElement | null;
  private readonly rivalLabelEl: HTMLElement | null;
  private readonly rivalTimeEl: HTMLElement | null;
  private readonly rivalGapEl: HTMLElement | null;
  private readonly splitTimeEls: Array<HTMLElement | null>;
  private readonly splitDeltaEls: Array<HTMLElement | null>;
  private readonly promotionDeltaEl: HTMLElement | null;
  private readonly promotionCopyEl: HTMLElement | null;
  private readonly recordRowEl: HTMLElement | null;
  private readonly recordLapEl: HTMLElement | null;
  private readonly recordHolderEl: HTMLElement | null;
  private readonly wrongWayEl: HTMLElement | null;
  private leaderboardPeekTimer: number | null = null;
  private leaderboardPeekActive = false;
  private leaderboardInspectActive = false;
  private targetSplitTimes: [number, number, number] | null = null;
  private targetSplitTitle = 'PB Sectors';
  private livePosition: LivePosition | null = null;

  constructor(private readonly timer: LapTimer) {
    this.root = getEl('hud');
    this.currentLapEl = getEl('hud-current-lap');
    this.bestLapEl = getEl('hud-best-lap');
    this.lastLapEl = document.getElementById('hud-last-lap');
    this.deltaPbEl = document.getElementById('hud-delta-pb');
    this.speedEl = getEl('hud-speed');
    this.driftEl = document.getElementById('hud-drift');
    this.driftFillEl = document.getElementById('hud-drift-fill');
    this.sectorNumEl = getEl('hud-sector-num');
    this.currentSectorEl = getEl('hud-current-sector');
    this.bestSectorEl = getEl('hud-best-sector');
    this.pausedEl = getEl('hud-paused');
    this.ghostTrackerEl = document.getElementById('hud-ghost-tracker');
    this.ghostTitleEl = this.ghostTrackerEl?.querySelector('.hud-ghost-title') ?? null;
    this.ghostLabelEl = document.getElementById('hud-ghost-label');
    this.ghostDeltaEl = document.getElementById('hud-ghost-delta');
    this.ghostStatusEl = document.getElementById('hud-ghost-status');
    this.targetRowEl = document.getElementById('hud-target-row');
    this.targetLabelEl = document.getElementById('hud-target-label');
    this.targetTimeEl = document.getElementById('hud-target-time');
    this.targetNoteEl = document.getElementById('hud-target-note');
    this.splitPanelEl = document.getElementById('hud-split-panel');
    this.splitTitleEl = this.splitPanelEl?.querySelector('.hud-split-title') ?? null;
    // Leaderboard and record rows only exist in the live HUD (not in the
    // trimmed test fixture), so be lenient about their presence.
    this.leaderboardBoardEl = document.getElementById('hud-timing-board');
    this.leaderboardRowsEl = document.getElementById('hud-leaderboard-rows');
    this.rivalRankEl = document.getElementById('hud-rival-rank');
    this.rivalLabelEl = document.getElementById('hud-rival-label');
    this.rivalTimeEl = document.getElementById('hud-rival-time');
    this.rivalGapEl = document.getElementById('hud-rival-gap');
    this.splitTimeEls = [1, 2, 3].map((n) => document.getElementById(`hud-split-${n}-time`));
    this.splitDeltaEls = [1, 2, 3].map((n) => document.getElementById(`hud-split-${n}-delta`));
    this.promotionDeltaEl = document.getElementById('hud-promo-delta');
    this.promotionCopyEl = document.getElementById('hud-promo-copy');
    this.recordRowEl = document.getElementById('hud-record-row');
    this.recordLapEl = document.getElementById('hud-record-lap');
    this.recordHolderEl = document.getElementById('hud-record-holder');
    this.wrongWayEl = document.getElementById('hud-wrong-way');
  }

  setWrongWay(active: boolean): void {
    if (!this.wrongWayEl) return;
    this.wrongWayEl.hidden = !active;
  }

  setRaceTarget(state: RaceTargetState): void {
    if (!this.targetLabelEl || !this.targetTimeEl || !this.targetNoteEl) return;
    this.targetLabelEl.textContent = state.label || 'Rival';
    this.targetTimeEl.textContent = formatHudTime(state.totalMs);
    const note = state.note ?? this.defaultRaceTargetNote(state);
    this.targetNoteEl.textContent = note;
    this.targetRowEl?.classList.remove('is-ahead', 'is-behind', 'is-empty');
    if (state.totalMs === null) this.targetRowEl?.classList.add('is-empty');
    const pbDelta = this.targetDeltaFromPersonalBest(state.totalMs);
    if (pbDelta !== null) {
      this.targetRowEl?.classList.add(pbDelta > 0 ? 'is-behind' : 'is-ahead');
    }
  }

  setLastLap(totalMs: number | null): void {
    if (!this.lastLapEl) return;
    this.lastLapEl.textContent = formatHudTime(totalMs);
  }

  /**
   * Set the global track record displayed in the top-right panel.
   * Pass `null` to hide the row entirely (no record fetched / offline).
   * `isMine` switches the color to amber when the player owns it.
   */
  setRecord(totalMs: number | null, holder: string | null, isMine: boolean): void {
    if (!this.recordRowEl || !this.recordLapEl || !this.recordHolderEl) return;
    if (totalMs === null) {
      this.recordRowEl.hidden = false;
      this.recordLapEl.textContent = '--:--.--';
      this.recordHolderEl.textContent = '';
      this.recordLapEl.classList.remove('is-mine');
      return;
    }
    this.recordRowEl.hidden = false;
    this.recordLapEl.textContent = formatHudTime(totalMs);
    this.recordLapEl.classList.toggle('is-mine', isMine);
    this.recordHolderEl.textContent = isMine
      ? 'You'
      : (holder ? formatHandle(holder) : '');
  }

  setLeaderboardRows(rows: LeaderboardHudRow[]): void {
    const visibleRows = rows.filter((row) => row.kind !== 'empty');
    if (visibleRows.length === 0) {
      this.leaderboardRowsEl?.replaceChildren(this.renderBoardRow({
        kind: 'empty',
        rank: null,
        handle: 'No PB yet',
        totalMs: null,
      }));
      this.updatePromotion([]);
      this.updateRivalSummary([]);
      return;
    }
    this.leaderboardRowsEl?.replaceChildren(
      ...visibleRows.map((row) => this.renderBoardRow(row)),
    );
    this.updatePromotion(visibleRows);
    this.updateRivalSummary(visibleRows);
  }

  setLeaderboardInspect(active: boolean): void {
    this.leaderboardInspectActive = active;
    this.syncLeaderboardExpansion();
  }

  showLeaderboardMoment(durationMs = 2800): void {
    if (!this.leaderboardBoardEl) return;
    this.leaderboardPeekActive = true;
    this.syncLeaderboardExpansion();
    if (this.leaderboardPeekTimer !== null) {
      window.clearTimeout(this.leaderboardPeekTimer);
    }
    this.leaderboardPeekTimer = window.setTimeout(() => {
      this.leaderboardPeekTimer = null;
      this.leaderboardPeekActive = false;
      this.syncLeaderboardExpansion();
    }, durationMs);
  }

  setGhostTracker(state: GhostTrackerState | null): void {
    if (!this.ghostTrackerEl || !this.ghostLabelEl || !this.ghostDeltaEl) return;
    if (!state) {
      this.ghostTrackerEl.hidden = true;
      this.ghostLabelEl.textContent = '';
      this.ghostDeltaEl.textContent = '--.--';
      this.ghostDeltaEl.classList.remove('sector-green', 'sector-red', 'sector-yellow');
      this.ghostTrackerEl.classList.remove('is-ahead', 'is-behind', 'is-pinned', 'is-single-label');
      this.ghostTrackerEl.style.left = '';
      this.ghostTrackerEl.style.top = '';
      if (this.ghostTitleEl) {
        this.ghostTitleEl.hidden = false;
        this.ghostTitleEl.textContent = 'Target Ghost';
      }
      if (this.ghostStatusEl) this.ghostStatusEl.textContent = '';
      return;
    }
    this.ghostTrackerEl.hidden = false;
    this.ghostTrackerEl.classList.add('is-single-label');
    if (this.ghostTitleEl) {
      this.ghostTitleEl.hidden = true;
      this.ghostTitleEl.textContent = state.title ?? 'Target Ghost';
    }
    this.ghostLabelEl.textContent = state.label;
    this.ghostDeltaEl.textContent = formatSignedDelta(state.deltaMs);
    this.ghostDeltaEl.classList.remove('sector-green', 'sector-red', 'sector-yellow');
    this.ghostTrackerEl.classList.remove('is-ahead', 'is-behind', 'is-pinned');
    if (state.deltaMs !== null) {
      if (state.deltaMs < 0) {
        this.ghostDeltaEl.classList.add('sector-green');
        this.ghostTrackerEl.classList.add('is-ahead');
      } else if (state.deltaMs > 0) {
        this.ghostDeltaEl.classList.add('sector-red');
        this.ghostTrackerEl.classList.add('is-behind');
      }
    }
    if (state.screen) {
      this.ghostTrackerEl.style.left = `${state.screen.x}px`;
      this.ghostTrackerEl.style.top = `${state.screen.y}px`;
      this.ghostTrackerEl.classList.toggle('is-pinned', state.screen.pinned);
    } else {
      this.ghostTrackerEl.style.left = '';
      this.ghostTrackerEl.style.top = '';
    }
    if (this.ghostStatusEl) {
      this.ghostStatusEl.textContent = ghostTrackerStatus(state.deltaMs, Boolean(state.screen?.pinned));
    }
  }

  setTargetSplits(sectors: [number, number, number] | null, title = 'Target Splits'): void {
    this.targetSplitTimes = sectors;
    this.targetSplitTitle = sectors ? title : 'PB Sectors';
    if (this.splitTitleEl) this.splitTitleEl.textContent = this.targetSplitTitle;
  }

  /** Feed in the live arc-position context the racing loop already tracks
   *  for the ghost. Drives ΔPB and the active-sector split delta as
   *  position-based gaps, not partial-vs-full subtractions. Pass null to
   *  clear (e.g. between runs). */
  setLivePosition(position: LivePosition | null): void {
    this.livePosition = position;
  }

  show(): void {
    this.root.hidden = false;
  }

  /** Called by main.ts whenever a sector closes — flashes the current
   *  sector readout green/purple to acknowledge the crossing. */
  notifySectorClose(
    isPersonalBest: boolean,
    _delta: number | null = null,
    _sector: 0 | 1 | 2 | null = null,
  ): void {
    this.flashSector(isPersonalBest);
  }

  notifyLapClose(isPersonalBest = false, totalMs: number | null = null): void {
    if (totalMs !== null) this.setLastLap(totalMs);
    this.bestLapEl.classList.remove('sector-purple');
    this.showLeaderboardMoment(4300);
    if (isPersonalBest) {
      this.bestLapEl.classList.add('sector-purple');
      setTimeout(() => {
        this.bestLapEl.classList.remove('sector-purple');
      }, 2000);
    }
  }

  notifyReset(): void {
    if (this.leaderboardPeekTimer !== null) {
      window.clearTimeout(this.leaderboardPeekTimer);
      this.leaderboardPeekTimer = null;
    }
    this.leaderboardPeekActive = false;
    this.leaderboardInspectActive = false;
    this.syncLeaderboardExpansion();
  }

  setPaused(paused: boolean): void {
    this.pausedEl.hidden = !paused;
  }

  /** Update the boost charge rail. `charge` is 0..1 and `boosting` is
   * true while the player is actively spending charge. */
  setDrift(charge: number, boosting: boolean): void {
    if (!this.driftEl || !this.driftFillEl) return;
    const c = Math.max(0, Math.min(1, charge));
    this.driftFillEl.style.transform = `scaleX(${c})`;
    this.driftEl.classList.toggle('is-ready', c >= 0.05);
    this.driftEl.classList.toggle('is-boosting', boosting);
  }

  /** Called every render frame with the current wall-clock time. */
  update(now: number, speedMps: number): void {
    const s = this.timer.state;
    const liveLap = s.running ? Math.max(0, now - s.currentLapStart) : 0;
    const liveSector = s.running ? Math.max(0, now - s.currentSectorStart) : 0;

    this.currentLapEl.textContent = formatHudTime(liveLap);
    this.bestLapEl.textContent = formatHudTime(s.bestLap);
    if (this.deltaPbEl) {
      const delta = s.bestLap === null || !s.running
        ? null
        : this.computePbLapDelta(liveLap);
      this.deltaPbEl.textContent = formatSignedDelta(delta);
      applyDeltaClass(this.deltaPbEl, delta);
    }
    // Hero speedometer shows just the padded km/h number; the "KM/H"
    // unit is a static label in the DOM next to it.
    this.speedEl.textContent = formatSpeedNumber(speedMps);

    this.sectorNumEl.textContent = String(s.currentSectorIndex + 1);
    this.currentSectorEl.textContent = formatHudSectorTime(liveSector);

    const bestSector = s.bestSectors[s.currentSectorIndex];
    this.bestSectorEl.textContent = formatHudSectorTime(bestSector);
    applyDeltaClass(this.bestSectorEl, bestSector !== null && s.running ? liveSector - bestSector : null);

    this.updateSplitRows(liveSector);
  }

  private flashSector(isPersonalBest: boolean): void {
    const el = this.currentSectorEl;
    el.classList.remove('sector-purple', 'sector-green');
    el.classList.add(isPersonalBest ? 'sector-purple' : 'sector-green');
    setTimeout(() => {
      el.classList.remove('sector-purple', 'sector-green');
    }, 700);
  }

  private renderBoardRow(row: LeaderboardHudRow): HTMLElement {
    const el = document.createElement('div');
    el.className = `hud-board-entry is-${row.kind}`;

    const rankEl = document.createElement('div');
    rankEl.className = 'hud-board-entry-rank';
    rankEl.textContent = row.rank === null ? '--' : `#${row.rank}`;

    const handleEl = document.createElement('div');
    handleEl.className = 'hud-board-entry-handle';
    handleEl.textContent = formatHandle(row.handle);

    const timeEl = document.createElement('div');
    timeEl.className = 'hud-board-entry-time';
    timeEl.textContent = formatHudTime(row.totalMs);

    el.append(rankEl, handleEl, timeEl);
    const deltaEl = document.createElement('div');
    deltaEl.className = 'hud-board-entry-delta';
    if (row.kind === 'mine') {
      deltaEl.textContent = 'PB';
    } else if (row.deltaMs !== undefined && row.deltaMs !== null) {
      deltaEl.textContent = formatSignedDelta(row.deltaMs);
    } else {
      deltaEl.classList.add('is-empty');
      deltaEl.textContent = '';
    }
    el.append(deltaEl);
    return el;
  }

  private updateSplitRows(liveSector: number): void {
    const s = this.timer.state;
    const comparisonSectors = this.targetSplitTimes ?? s.bestSectors;
    const hasComparableSectors = comparisonSectors.some((best) => best !== null);
    if (this.splitTitleEl) this.splitTitleEl.textContent = this.targetSplitTitle;
    const activeDelta = this.computeActiveSectorDelta(comparisonSectors);
    for (let i = 0; i < 3; i++) {
      const timeEl = this.splitTimeEls[i];
      const deltaEl = this.splitDeltaEls[i];
      const sectorTime = getVisibleSectorTime(s, i as 0 | 1 | 2, liveSector);
      if (timeEl) timeEl.textContent = formatHudTime(sectorTime);
      if (!deltaEl) continue;
      const reference = comparisonSectors[i];
      let delta: number | null;
      if (reference === null || sectorTime === null) {
        delta = null;
      } else if (i < s.currentSectorIndex) {
        // Sector is closed — full-sector vs full-sector delta is meaningful.
        delta = sectorTime - reference;
      } else if (i === s.currentSectorIndex) {
        // Active sector — show position-based gap so it ticks like the
        // ghost arrow instead of starting at -reference and crawling up.
        delta = activeDelta;
      } else {
        delta = null;
      }
      deltaEl.textContent = delta === null ? '' : formatSignedDelta(delta);
      applyDeltaClass(deltaEl, delta);
    }
    this.splitPanelEl?.classList.toggle('is-empty', !hasComparableSectors);
  }

  private computePbLapDelta(liveLap: number): number | null {
    const pos = this.livePosition;
    if (!pos || pos.lapMs === null || pos.progress === null) return null;
    if (pos.pbSamples.length === 0) return null;
    const ghostElapsed = ghostElapsedAtProgress(pos.pbSamples, pos.progress);
    if (ghostElapsed === null) return null;
    // Prefer the fixed-step lap accumulator when available — it's what the
    // ghost samples are measured against. Falls back to the render-clock
    // lap when no live position has been set (tests, idle states).
    const liveMs = pos.lapMs ?? liveLap;
    return liveMs - ghostElapsed;
  }

  private computeActiveSectorDelta(
    comparisonSectors: ReadonlyArray<number | null>,
  ): number | null {
    const s = this.timer.state;
    const pos = this.livePosition;
    if (!pos || pos.lapMs === null || pos.progress === null) return null;
    const samples = pos.comparisonSamples.length > 0
      ? pos.comparisonSamples
      : pos.pbSamples;
    if (samples.length === 0) return null;
    const ghostElapsedNow = ghostElapsedAtProgress(samples, pos.progress);
    if (ghostElapsedNow === null) return null;
    // Sum the comparison's elapsed time at the start of the active sector
    // by walking the prior reference sectors.
    let ghostElapsedAtSectorStart = 0;
    for (let i = 0; i < s.currentSectorIndex; i++) {
      const ref = comparisonSectors[i];
      if (ref === null) return null;
      ghostElapsedAtSectorStart += ref;
    }
    const liveSectorElapsed = pos.lapMs - sumClosedSectors(s);
    const ghostSectorElapsed = ghostElapsedNow - ghostElapsedAtSectorStart;
    return liveSectorElapsed - ghostSectorElapsed;
  }

  private updatePromotion(rows: LeaderboardHudRow[]): void {
    if (!this.promotionDeltaEl || !this.promotionCopyEl) return;
    const mineIndex = rows.findIndex((row) => row.kind === 'mine');
    const mine = mineIndex >= 0 ? rows[mineIndex] : null;
    if (!mine || mine.totalMs === null) {
      this.promotionDeltaEl.textContent = '--.--';
      this.promotionCopyEl.textContent = 'Set a PB to enter the ladder';
      return;
    }
    const faster = rows
      .slice(0, mineIndex)
      .reverse()
      .find((row) => row.totalMs !== null);
    if (!faster || faster.totalMs === null) {
      this.promotionDeltaEl.textContent = 'P1';
      this.promotionCopyEl.textContent = 'Defend the record';
      return;
    }
    this.promotionDeltaEl.textContent = formatPromotionGap(mine.totalMs - faster.totalMs);
    this.promotionCopyEl.textContent = 'Beat target / improve PB';
  }

  private updateRivalSummary(rows: LeaderboardHudRow[]): void {
    if (!this.rivalRankEl || !this.rivalLabelEl || !this.rivalTimeEl || !this.rivalGapEl) return;
    this.leaderboardBoardEl?.classList.remove('is-record-target', 'is-record-held');
    const mineIndex = rows.findIndex((row) => row.kind === 'mine');
    const mine = mineIndex >= 0 ? rows[mineIndex] : null;
    if (mine && mine.totalMs !== null) {
      const faster = rows
        .slice(0, mineIndex)
        .reverse()
        .find((row) => row.totalMs !== null);
      if (faster && faster.totalMs !== null) {
        this.rivalRankEl.textContent = formatRank(faster.rank);
        this.rivalLabelEl.textContent = faster.handle;
        this.rivalTimeEl.textContent = formatHudTime(faster.totalMs);
        const gap = `Need ${formatPromotionGap(mine.totalMs - faster.totalMs)}`;
        this.rivalGapEl.textContent = faster.hasGhost === false
          ? `${gap} / no ghost`
          : gap;
        return;
      }
      this.rivalRankEl.textContent = formatRank(mine.rank);
      this.rivalLabelEl.textContent = 'Defend Record';
      this.rivalTimeEl.textContent = formatHudTime(mine.totalMs);
      this.rivalGapEl.textContent = 'P1 record defense';
      this.leaderboardBoardEl?.classList.add('is-record-held');
      return;
    }

    const record = rows.find((row) => row.kind === 'record' && row.totalMs !== null)
      ?? rows.find((row) => row.totalMs !== null);
    if (record && record.totalMs !== null) {
      this.rivalRankEl.textContent = formatRank(record.rank);
      this.rivalLabelEl.textContent = `Record ${record.handle}`.trim();
      this.rivalTimeEl.textContent = formatHudTime(record.totalMs);
      this.rivalGapEl.textContent = 'Set PB to enter ladder';
      this.leaderboardBoardEl?.classList.add('is-record-target');
      return;
    }

    this.rivalRankEl.textContent = '--';
    this.rivalLabelEl.textContent = 'No PB yet';
    this.rivalTimeEl.textContent = '--:--.--';
    this.rivalGapEl.textContent = 'Run a clean lap';
  }

  private defaultRaceTargetNote(state: RaceTargetState): string {
    if (state.totalMs === null) return 'Set a clean lap';
    const delta = this.targetDeltaFromPersonalBest(state.totalMs);
    if (delta === null) return 'Set a clean lap';
    if (Math.abs(delta) < 1) return 'Beat PB';
    return delta > 0
      ? `Need ${formatPromotionGap(delta)}`
      : `Ahead ${formatSignedDelta(delta)}`;
  }

  private targetDeltaFromPersonalBest(totalMs: number | null): number | null {
    const best = this.timer.state.bestLap;
    if (totalMs === null || best === null || !Number.isFinite(best)) return null;
    return Math.round(best) - totalMs;
  }

  private syncLeaderboardExpansion(): void {
    const expanded = this.leaderboardPeekActive || this.leaderboardInspectActive;
    this.leaderboardBoardEl?.classList.toggle('is-expanded', expanded);
    this.root.classList.toggle('is-ladder-expanded', expanded);
  }

}

export function formatSpeed(metersPerSecond: number): string {
  return formatSpeedNumber(metersPerSecond) + ' km/h';
}

/** Padded km/h integer with no unit suffix — used by the hero speedometer. */
export function formatSpeedNumber(metersPerSecond: number): string {
  const kmh = Math.max(0, Math.round(metersPerSecond * 3.6));
  if (kmh >= 1000) return kmh.toLocaleString('en-US');
  return String(kmh).padStart(3, '0');
}

export function formatSignedDelta(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '--.--';
  const sign = ms < 0 ? '-' : '+';
  const hundredths = Math.floor(Math.abs(ms) / 10);
  const seconds = Math.floor(hundredths / 100);
  const fraction = hundredths % 100;
  return `${sign}${seconds}.${String(fraction).padStart(2, '0')}`;
}

function formatPromotionGap(ms: number): string {
  if (!Number.isFinite(ms)) return '--.--';
  return formatSignedDelta(Math.abs(ms)).replace('-', '+');
}

function formatRank(rank: number | null): string {
  return rank === null ? '--' : `#${rank}`;
}

function ghostTrackerStatus(deltaMs: number | null, pinned: boolean): string {
  if (deltaMs === null) return pinned ? 'Offscreen' : 'Tracking';
  if (deltaMs < 0) return pinned ? 'Ghost Behind' : 'Ahead';
  if (deltaMs > 0) return pinned ? 'Offscreen' : 'Behind';
  return pinned ? 'Offscreen' : 'Even';
}

function applyDeltaClass(el: HTMLElement, delta: number | null): void {
  el.classList.remove('sector-green', 'sector-red', 'sector-yellow');
  if (delta === null) return;
  if (delta < 0) el.classList.add('sector-green');
  else if (delta > 0) el.classList.add('sector-red');
}

function sumClosedSectors(state: LapTimer['state']): number {
  let total = 0;
  for (let i = 0; i < state.currentSectorIndex; i++) total += state.currentSectorTimes[i];
  return total;
}

function getVisibleSectorTime(
  state: LapTimer['state'],
  sector: 0 | 1 | 2,
  liveSector: number,
): number | null {
  if (!state.running) return null;
  if (sector < state.currentSectorIndex) return state.currentSectorTimes[sector];
  if (sector === state.currentSectorIndex) return liveSector;
  return null;
}

function formatHudTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '--:--.--';
  const totalHundredths = Math.floor(ms / 10);
  const minutes = Math.floor(totalHundredths / 6000);
  const seconds = Math.floor((totalHundredths % 6000) / 100);
  const hundredths = totalHundredths % 100;
  return (
    String(minutes).padStart(2, '0') +
    ':' +
    String(seconds).padStart(2, '0') +
    '.' +
    String(hundredths).padStart(2, '0')
  );
}

function formatHudSectorTime(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return '--.--';
  const totalHundredths = Math.floor(ms / 10);
  const seconds = Math.floor(totalHundredths / 100);
  const hundredths = totalHundredths % 100;
  return String(seconds).padStart(2, '0') + '.' + String(hundredths).padStart(2, '0');
}

function getEl(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`HUD element #${id} not found`);
  return el;
}
